import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map } from 'rxjs/operators';

import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { Brand } from 'src/app/shared/models/brand';
import { combine } from 'src/app/shared/utilities/route';
import { PagingToken, FetchArgs } from 'src/app/shared/models/core';
import { FacilityHospital } from 'src/app/shared/models/facility-hospital.model';
import { Lookup } from 'src/app/shared/models/lookup.model';
import { Observable } from 'rxjs';
import { HierarchicalOption } from '../models/hierarchical-option.model';
import { FacilityNameAndAddress } from '../models/facility-name-and-address.model';
import { FacilityContact } from '../lib/travel-contracts-contacts/models/facility-contact.model';
import { CountryState } from '../models/lookup-country-states.model';
import { CountryIds } from '../enums/countries.enum';

@Injectable({
    providedIn: 'root'
})
export class HospitalsService {
    private coreApiPath: string;

    constructor(
        @Inject(APP_CONFIG) private readonly settings: Settings,
        private readonly http: HttpClient
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
    }

    searchHospitals(args: FetchArgs): Observable<PagingToken<FacilityHospital[]>> {
        const url = `${this.coreApiPath}/hospital/search`;
        return this.postPaged(url, args);
    }

    lookupHospitalSystem(keyword: string): Observable<Lookup[]> {
        const search = '?keyword=' + encodeURIComponent(keyword);
        const url = `${this.coreApiPath}/hospital/systems${search}`;
        return this.http.get<Lookup[]>(url);
    }

    lookupHospitalSystemsHierarchy(): Observable<HierarchicalOption[]> {
        const url = `${this.coreApiPath}/facility-contracts/facility-profiles/facility-systems`;
        return this.http.get<HierarchicalOption[]>(url);
    }

    lookupClassification(searchText: string): Observable<Lookup[]> {
        const search = '?keyword=' + searchText ? encodeURIComponent(searchText) : '%A0';
        const url = `${this.coreApiPath}/hospital/hospital-classifications${search}`;
        return this.http.get<Lookup[]>(url);
    }

    lookupContractedGroup(searchText: string): Observable<Lookup[]> {
        const search = '?keyword=' + encodeURIComponent(searchText);
        const url = `${this.coreApiPath}/hospital/contract-groups${search}`;
        return this.http.get<Lookup[]>(url);
    }

    lookupVMSProvider(searchText: string): Observable<Lookup[]> {
        const search = '?keyword=' + encodeURIComponent(searchText);
        const url = `${this.coreApiPath}/hospital/vms-providers${search}`;
        return this.http.get<Lookup[]>(url);
    }

    lookupAccountManager(keyword: string = ''): Observable<Lookup[]> {
        const search = '?keyword=' + encodeURIComponent(keyword);
        const url = `${this.coreApiPath}/ClientServices/account-managers${search}`;

        return this.http.get<Lookup[]>(url);
    }

    lookupStates(): Observable<CountryState[]> {
        const url = `${this.coreApiPath}/states`;
        return this.http
            .get<CountryState[]>(url)
            .pipe(
                map((res) =>
                    res.filter(
                        (x) =>
                            x.country.id === CountryIds.US ||
                            x.country.id === CountryIds.Guam ||
                            x.country.id === CountryIds.USVirginIslands
                    )
                )
            );
    }

    lookupFacilityType(): Observable<Lookup[]> {
        const url = `${this.coreApiPath}/hospital/facilities-types`;
        return this.http.get<Lookup[]>(url);
    }

    lookupLinesOfBusiness(): Observable<Lookup[]> {
        const url = `${this.coreApiPath}/hospital/line-of-business`;
        return this.http.get<Lookup[]>(url);
    }

    lookupContractStatus(): Observable<Lookup[]> {
        const url = `${this.coreApiPath}/hospital/contract-statuses`;
        return this.http.get<Lookup[]>(url);
    }

    getBrands(): Observable<Brand[]> {
        return this.http.get<Brand[]>(`${this.coreApiPath}/facility/brands`);
    }

    getFacilityAddress(facilityId: number): Observable<FacilityNameAndAddress> {
        const url = `${this.coreApiPath}/facility/${facilityId}/address`;
        return this.http.get<FacilityNameAndAddress>(url);
    }

    addEditFacilityContact(contact: FacilityContact): Observable<number> {
        const url = `${this.coreApiPath}/clientservices/contact/add-update`;

        return this.http.post<number>(url, contact);
    }

    getRatings(): Observable<Lookup[]> {
        return this.http.get<Lookup[]>(`${this.coreApiPath}/facility/ratings`);
    }

    deleteFacilityUnitContact(unitId: number, facilityId: number): Observable<number> {
        const url = `${this.coreApiPath}/clientservices/contact/${unitId}/${facilityId}`;

        return this.http.delete<number>(url);
    }

    protected postPaged<T>(url: string, fetchArgs: FetchArgs): Observable<PagingToken<T[]>> {
        return this.http.post<PagingToken<T[]>>(url, fetchArgs, { observe: 'response' }).pipe(
            map((response) => {
                return {
                    total: response.body.total,
                    data: response.body.data
                };
            })
        );
    }
}
